"use strict";


class Tab {

	keys = {
		end: "End",
		home: "Home",
		left: "ArrowLeft",
		right: "ArrowRight"
	};

	// Add or substract depending on key pressed
	direction = {
		"ArrowLeft": -1,
		"ArrowRight": 1
	};

	constructor(elem, params = {}) {
		if (!elem.classList.contains("content-tabs-initialized")) {
			this.elem = elem;
			this.elem.classList.add("content-tabs-initialized");

			this.tabs = elem.querySelectorAll('[role="tab"]');
			this.panels = elem.querySelectorAll('[role="tabpanel"]');
			this.activeClass = params.activeClass || null;

			this.delay = this.elem.dataset.delay || 0;

			this.addEvents();
		}
	}

	/**
	 * addEvents
	 */
	addEvents() {
		this.tabs.forEach((tab) => {
			tab.addEventListener("click", (e) => { this.onClickEvent(e); });
			tab.addEventListener("keydown", (e) => { this.onKeyDownEvent(e); });
			tab.addEventListener("keyup", (e) => { this.onKeyUpEvent(e); });
			tab.addEventListener("focus", (e) => { this.onFocusEvent(e); });
		});

		// Save tab index
		this.tabs.forEach((tab, i) => {
			tab.index = i;
		});
	}

	/**
	 * onClickEvent
	 * @param {Event} event
	 * @returns {void}
	 */
	onClickEvent(event) {
		event.preventDefault();
		const tab = event.currentTarget;
		this.activateTab(tab, false);
	}

	/**
	 * onKeyDownEvent
	 * @param {Event} event
	 * @returns {void}
	 */
	onKeyDownEvent(event) {
		const key = event.key;

		switch (key) {
			case this.keys.end: {
				event.preventDefault();
				// Activate last tab
				const lastTab = Array.from(this.tabs).filter((tab) => !tab.disabled).pop();
				if (lastTab) {
					this.activateTab(lastTab);
				}
				break;
			}
			case this.keys.home: {
				event.preventDefault();
				// Activate first tab
				const firstTab = Array.from(this.tabs).filter((tab) => !tab.disabled).shift();
				if (firstTab) {
					this.activateTab(firstTab);
				}
				break;
			}

		}
	}

	/**
	 * onKeyUpEvent
	 * @param {Event} event
	 * @returns {void}
	 */
	onKeyUpEvent(event) {
		const key = event.key;

		switch (key) {
			case this.keys.left:
			case this.keys.right:
				this.switchTab(event);
				break;
		}
	}

	/**
	 * onFocusEvent
	 * @param {Event} event
	 * @returns {void}
	 */
	onFocusEvent(event) {
		const target = event.currentTarget;
		const context = this;

		this.tabs.forEach((tab) => {
			if (tab !== target) {
				tab.setAttribute("tabindex", "-1");
				tab.setAttribute("aria-selected", "false");
			}
		});

		setTimeout(function () {
			context.checkTabFocus(target);
		}, this.delay);
	}

	/**
	 * deactivateTabs
	 * @returns {void}
	 */
	deactivateTabs() {
		this.tabs.forEach((tab) => {
			tab.setAttribute("tabindex", "-1");
			tab.setAttribute("aria-selected", "false");
		});

		this.panels.forEach((panel) => {
			panel.setAttribute("hidden", "hidden");
			if (this.activeClass) {
				panel.classList.remove(this.activeClass);
			}
		});
	}

	/**
	 * activateTab
	 * @param {HTMLElement} tab
	 * @param {boolean} setFocus
	 * @returns {void}
	 */
	activateTab(tab, setFocus = true) {

		if (!tab || tab?.disabled || (tab?.getAttribute("aria-selected") === "true")) {
			return;
		}

		this.deactivateTabs();

		// Remove tabindex attribute
		tab.removeAttribute("tabindex");

		// Set the tab as selected
		tab.setAttribute("aria-selected", "true");

		// Get the value of aria-controls (which is an ID)
		const controlId = tab.getAttribute("aria-controls");
		const activeTabContent = document.getElementById(controlId);
		if (activeTabContent) {

			// Remove hidden attribute from tab panel to make it visible
			activeTabContent.removeAttribute("hidden");

			if (this.activeClass) {
				activeTabContent.classList.add(this.activeClass);
			}
		}

		// Set focus when required
		if (setFocus) {
			tab.focus();
		}

		const eventOpen = new CustomEvent("tab.open", {
			bubbles: true,
			detail: { contentId: controlId }
		});

		// Dispatch open event
		tab.dispatchEvent(eventOpen);
	}

	/**
	 * switchTab
	 * @param {Event} event
	 * @returns {void}
	 */
	switchTab(event) {
		const pressed = event.key;

		if (this.activeClass) {
			const target = event.currentTarget;
			if (target.index !== undefined) {
				if (this.tabs[target.index + this.direction[pressed]]) {
					this.activateTab(this.tabs[target.index + this.direction[pressed]]);
				}
				else if ((pressed === this.keys.left) || (pressed === this.keys.up)) {
					this.focusLastTab();
				}
				else if ((pressed === this.keys.right) || (pressed === this.keys.down)) {
					this.focusFirstTab();
				}
			}
		}
	}

	/**
	 * focusFirstTab
	 * @returns {void}
	 */
	focusFirstTab() {
		this.tabs[0]?.focus();
	}

	/**
	 * focusLastTab
	 * @returns {void}
	 */
	focusLastTab() {
		this.tabs[this.tabs.length - 1]?.focus();
	}

	/**
	 * checkTabFocus
	 * @param {HTMLElement} target
	 * @returns {void}
	 */
	checkTabFocus(target) {
		const focused = document.activeElement;

		if (target === focused) {
			this.activateTab(target, false);
		}

	}
}

export default (selector = ".content-tabs, .m-tabs") => {

	const tabElems = document.querySelectorAll(`${selector}:not(.content-tabs-initialized)`);
	tabElems.forEach((element) => {
		const params = {};
		if (element.classList.contains("m-tabs")) {
			params.activeClass = "m-tabs__content--active";
		}
		new Tab(element, params);
	}
	);
};
